import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const ImageCropper = ({ 
  imageSrc, 
  onCropComplete, 
  onCancel, 
  aspectRatio = 1, // 1:1 مربع، 16/9 للمستطيل، null للحرية الكاملة
  minWidth = 150,
  minHeight = 150 
}) => {
  const { t } = useTranslation();
  const imgRef = useRef(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);

  const onImageLoad = useCallback((e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    
    // تحديد حجم الاقتصاص الأولي بناءً على حجم الصورة
    const centerCrop = {
      unit: '%',
      width: 80,
      height: aspectRatio ? 80 : 80,
      x: 10,
      y: 10,
    };
    
    setCrop(centerCrop);
  }, [aspectRatio]);

  const getCroppedImg = useCallback(async (image, crop) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  }, []);

  const handleCropComplete = async () => {
    if (!imgRef.current || !completedCrop) return;

    try {
      const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop);
      
      // تحويل البلوب إلى ملف
      const croppedFile = new File([croppedImageBlob], 'cropped-image.jpg', {
        type: 'image/jpeg'
      });
      
      // إنشاء URL للمعاينة
      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      
      onCropComplete(croppedFile, croppedImageUrl);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleRotate = () => {
    setRotate(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setScale(1);
    setRotate(0);
    setCrop({
      unit: '%',
      width: 80,
      height: 80,
      x: 10,
      y: 10
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <PhotoIcon className="h-6 w-6 mr-2" />
                <h3 className="text-lg font-semibold">تعديل واقتصاص الصورة</h3>
              </div>
              <button
                onClick={onCancel}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleZoomOut}
                  className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="تصغير"
                >
                  <MagnifyingGlassMinusIcon className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium min-w-16 text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="تكبير"
                >
                  <MagnifyingGlassPlusIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleRotate}
                  className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="دوران"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-600">
                  {rotate}°
                </span>
              </div>

              <button
                onClick={handleReset}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                إعادة تعيين
              </button>

              <div className="mr-auto text-sm text-gray-600">
                <p>💡 اسحب لتحديد المنطقة المرغوبة</p>
              </div>
            </div>

            {/* Crop Area */}
            <div className="flex justify-center mb-6">
              <div 
                className="relative inline-block border border-gray-300 rounded-lg overflow-hidden"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '300px',
                  transform: `scale(${scale}) rotate(${rotate}deg)`
                }}
              >
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspectRatio}
                  minWidth={minWidth}
                  minHeight={minHeight}
                  keepSelection
                  ruleOfThirds
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '300px',
                      display: 'block'
                    }}
                  />
                </ReactCrop>
              </div>
            </div>

            {/* Preview */}
            {completedCrop && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">معاينة النتيجة:</h4>
                <div className="flex justify-center">
                  <div className="w-24 h-24 border border-gray-300 rounded-lg overflow-hidden bg-gray-100">
                    <canvas
                      ref={(canvas) => {
                        if (canvas && imgRef.current && completedCrop) {
                          const ctx = canvas.getContext('2d');
                          if (ctx) {
                            const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
                            const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
                            
                            canvas.width = 96;
                            canvas.height = 96;
                            
                            const cropWidth = completedCrop.width * scaleX;
                            const cropHeight = completedCrop.height * scaleY;
                            
                            ctx.drawImage(
                              imgRef.current,
                              completedCrop.x * scaleX,
                              completedCrop.y * scaleY,
                              cropWidth,
                              cropHeight,
                              0,
                              0,
                              96,
                              96
                            );
                          }
                        }
                      }}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">إرشادات سريعة:</h4>
              <div className="text-xs text-blue-800 space-y-1">
                <p>• اسحب الزوايا لتغيير الحجم • اسحب المنطقة لتحريكها</p>
                <p>• استخدم أزرار التكبير/التصغير والدوران حسب الحاجة</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-4">
              <button
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleCropComplete}
                disabled={!completedCrop}
                className="px-6 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <CheckIcon className="h-5 w-5 mr-2" />
                تطبيق وحفظ
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImageCropper;
